import React, { useState, useEffect } from "react";
import {
  useParams,
  useHistory,
  useRouteMatch,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { cartAction } from "../state/actions";
import Typography from "@material-ui/core/Typography";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import CardMedia from "@material-ui/core/CardMedia";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import ItemDescriptionDialog from "../components/ItemDescriptionDialog";
import FridgeConfirmationDialog from "../components/FridgeConfirmationDialog";
import { SellingAreaState, DietPreference } from "../state";
import DoneIcon from "@material-ui/icons/Done";
import InfoIcon from "@material-ui/icons/Info";
import AppBar from "../../../components/AppBar";
import HorizontalScrollContainer from "../../../components/HorizontalScrollContainer";
import LabelPreference from "../components/LabelPreference";
import { AllergensListLabel } from "../state/reducers/diet";

function isUnsellable(item: any): boolean {
  return item.expired === true;
}

function hasDietConflict(item: any, diet: DietPreference | null): boolean {
  if (!diet) return false;
  if (!item.edible) return true;

  let conditionDiet = false;
  let conditionAllergens = false;

  if (diet.diet) {
    conditionDiet = !({
      'Contiene derivati della carne o pesce': ['Contiene derivati della carne o pesce', 'Pescetariano', 'Vegetariano', 'Vegano'],
      Pescetariano: ['Pescetariano', 'Vegetariano', 'Vegano'],
      Vegetariano: ['Vegetariano', 'Vegano'],
      Vegano: ['Vegano'],
    }[diet.diet] || []).includes(item.edible.diet);
  }

  if (diet.allergens) {
    conditionAllergens = Object.keys(AllergensListLabel).map((key: string) => {
      if (!diet.allergens) return false;
      switch (diet.allergens[key as keyof typeof diet.allergens]) {
        case 'Presente': return false;
        case 'Tracce': return 'Presente' === item.edible[key];
        case 'Assente': return ['Presente', 'Tracce'].includes(item.edible[key]);
        default: return false;
      }
    }).includes(true);
  }

  return conditionDiet || conditionAllergens;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  header: {
    padding: theme.spacing(2, 3, 1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  container: {
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 2),
    },
  },
  cardMedia: {
    paddingTop: "100%",
  },
  cardContent: {
    height: "118px",
    padding: theme.spacing(1),
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  cardActions: {
    flexDirection: "column",
    padding: theme.spacing(1, 0, 0),
  },
  cardSelected: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  cardDefault: {
    border: "2px solid transparent",
  },
  actionButton: {
    margin: theme.spacing(0, 0, 1),
  },
  backButton: {
    margin: theme.spacing(0, 0, 0, 1),
  },
  title: {
    flexGrow: 1,
  },
  addToCartBar: {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1, 2, 2),
    backgroundColor: theme.palette.background.default,
    zIndex: 10,
    boxShadow: "0px -2px 5px rgba(0,0,0,0.1)",
  },
  bundlesContainer: {
    display: "flex",
    overflowX: "auto",
    flexWrap: "nowrap",
    gap: `${theme.spacing(2)}px`,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    padding: theme.spacing(1, 0),
  },
  bundleItem: {
    flex: "0 0 calc(50% - 16px)",
    position: "relative",
    [theme.breakpoints.up("sm")]: {
      flex: "0 0 calc(33% - 16px)",
    },
    [theme.breakpoints.up("md")]: {
      flex: "0 0 calc(25% - 16px)",
    },
  },
  selectedBadge: {
    position: "absolute",
    top: -8,
    right: -8,
    zIndex: 1,
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  categoryTitle: {
    padding: theme.spacing(3, 0),
  },
}));

export default function BundlePage() {
  const classes = useStyles();
  const history = useHistory();
  const { url } = useRouteMatch();
  const params = useParams<{ id: string; bundleId: string }>();
  const location = useLocation<{
    selectedByCategory?: Record<string, number>;
  }>();
  const dispatch = useDispatch();
  const wallet = useSelector((state: any) => state.wallet);
  const corporateCredit = useSelector((state: any) => state.corporateCredit);
  const cart = useSelector((state: any) => state.cart);
  const diet = useSelector((state: SellingAreaState) => state.diet) as DietPreference | null;
  const [selectedByCategory, setSelectedByCategory] = useState<
    Record<string, number>
  >(location.state?.selectedByCategory || {});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mySearchParams = new URLSearchParams(location.search);
  const isPreview = mySearchParams.get('mode') === 'preview';

  const handleClickOpen = (itemId: number) => {
    const sp = new URLSearchParams(location.search);
    sp.append('description', itemId.toString());
    history.push({ pathname: location.pathname, search: sp.toString() });
  };

  const handleClose = () => {
    history.goBack();
  };

  const openUnsellableItemInfoPopup = () => {
    const sp = new URLSearchParams(location.search);
    sp.append("unsellable-item-info-banner", "open");
    history.push({ pathname: location.pathname, search: sp.toString() });
  };

  const bundle = useSelector((state: SellingAreaState) => state.bundle);
  const catalog = useSelector((state: SellingAreaState) => state.catalog);

  const currentBundle = bundle?.find((b) => b.id === params.bundleId);

  const filteredCategories: any[] = React.useMemo(() => {
    if (!catalog || !currentBundle) return [];
    const categories: any[] = [];
    catalog.categories.forEach((category: any) => {
      if (currentBundle.categories.includes(category.id)) {
        const excluded = currentBundle.excludedByCategory?.[category.id] ?? [];
        categories.push({
          ...category,
          items: category.items.filter((i: any) => !excluded.includes(i.internalCategoryId)),
        });
      }
    });
    return categories;
  }, [catalog, currentBundle]);

  const descriptionItemId = mySearchParams.get('description');
  const descriptionItem = descriptionItemId
    ? filteredCategories.flatMap((c: any) => c.items).find((i: any) => i.id.toString() === descriptionItemId)
    : null;

  useEffect(() => {
    setSelectedByCategory((prev) => {
      const next = { ...prev };
      let changed = false;
      filteredCategories.forEach((category: any) => {
        const selectedId = next[category.id];
        if (selectedId !== undefined) {
          const item = category.items.find((i: any) => i.id === selectedId);
          if (item && isUnsellable(item)) {
            delete next[category.id];
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [filteredCategories]);

  const everythingSelected = filteredCategories.every((fc) => {
    const selectedId = selectedByCategory[fc.id];
    if (selectedId === undefined) return false;
    const item = fc.items.find((i: any) => i.id === selectedId);
    return item && !isUnsellable(item);
  });

  const isSelected = (item: any) =>
    selectedByCategory[item.categoryId] === item.id;

  const handleSelect = (item: any) => {
    setSelectedByCategory((prev) => {
      const next = { ...prev };
      if (next[item.categoryId] === item.id) {
        delete next[item.categoryId];
      } else {
        next[item.categoryId] = item.id;
      }
      return next;
    });
  };

  if (!currentBundle) return <CircularProgress />;

  return (
    <div
      className={classes.root}
      style={everythingSelected ? { paddingBottom: "80px" } : undefined}
    >
      <AppBar position="fixed" enableBack back={() => history.push(`/selling-area/${params.id}${location.search}`)} title="Il tuo menu" />

      <div className={classes.header}>
        <Typography variant="h4">{currentBundle.title}</Typography>
        {currentBundle?.subtitle && (
          <Typography variant="subtitle1" className={classes.subtitle}>
            {currentBundle.subtitle} a {currentBundle.defaultPrice}€
          </Typography>
        )}
      </div>
      <div className={classes.container}>
        {filteredCategories.map((category) => (
          <>
            <Typography
              variant="h5"
              color="inherit"
              className={classes.categoryTitle}
            >
              {category.label.toUpperCase()}
            </Typography>
            <HorizontalScrollContainer className={classes.bundlesContainer}>
              {category.items.map((item) => {
                const unsellable = isUnsellable(item);
                const dietConflict = hasDietConflict(item, diet);
                return (
                  <div className={classes.bundleItem} key={item.id}>
                    {isSelected(item) && (
                      <div className={classes.selectedBadge}>
                        <DoneIcon style={{ fontSize: 16, color: "white" }} />
                      </div>
                    )}
                    <Card
                      className={
                        isSelected(item)
                          ? classes.cardSelected
                          : classes.cardDefault
                      }
                    >
                      <CardActionArea onClick={() => handleClickOpen(item.id)}>
                        <CardMedia
                          className={classes.cardMedia}
                          image={item.image}
                          title={item.label}
                        />
                        {dietConflict && <LabelPreference label="escluso dal tuo filtro" />}
                        <CardContent className={classes.cardContent}>
                          <Typography variant="subtitle1" color="textPrimary">
                            {item.label}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                      <CardActions disableSpacing className={classes.cardActions}>
                        <Button
                          className={classes.actionButton}
                          color={unsellable ? "default" : "primary"}
                          variant="contained"
                          disabled={isSelected(item) && !unsellable}
                          endIcon={item.expired ? <InfoIcon /> : null}
                          onClick={() => {
                            if (item.expired) {
                              openUnsellableItemInfoPopup();
                            } else if (!unsellable) {
                              handleSelect(item);
                            }
                          }}
                        >
                          {unsellable ? "NON ACQUISTABILE" : "SELEZIONA"}
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleClickOpen(item.id)}
                        >
                          VEDI INGREDIENTI
                        </Button>
                      </CardActions>
                    </Card>
                  </div>
                );
              })}
            </HorizontalScrollContainer>
          </>
        ))}
      </div>
      <ItemDescriptionDialog
        item={descriptionItem}
        onClose={handleClose}
        onSelect={() => {
          if (isPreview) {
            history.replace(`${location.pathname}?mode=preview&leave=need-confirm`);
          } else {
            if (descriptionItem) handleSelect(descriptionItem);
            handleClose();
          }
        }}
      />
      <FridgeConfirmationDialog />
      {everythingSelected && (
        <div className={classes.addToCartBar}>
          {isPreview && (
            <Button
              fullWidth
              color="primary"
              variant="contained"
              size="large"
              onClick={() => {
                history.push(`/selling-area/${params.id}?mode=preview&leave=need-confirm`);
              }}
            >
              LO VOGLIO
            </Button>
          )}
          {!isPreview && <Button
            fullWidth
            color="primary"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                if (cart?.items?.length > 0) {
                  await Promise.all(
                    cart.items.map((cartItem: any) =>
                      cartAction.updateCartItem(dispatch, {
                        lockerId: params.id,
                        itemSaleableId: cartItem.itemSaleableId,
                        quantity: 0,
                        localPrice: cartItem.unitPriceWithTax,
                        localLabel: cartItem.label,
                        walletBalance: wallet?.balance || 0,
                        corporateCreditBalance: corporateCredit?.balance || 0,
                        bundleId: params.bundleId,
                      }),
                    ),
                  );
                }
                // Le aggiunte sono sequenziali: se non esiste un draft order, la prima chiamata
                // lo crea e le successive lo trovano già esistente, evitando la race condition.
                for (const category of filteredCategories) {
                  const itemId = selectedByCategory[category.id];
                  if (itemId !== undefined) {
                    const item = category.items.find((i: any) => i.id === itemId);
                    if (item) {
                      await cartAction.updateCartItem(dispatch, {
                        lockerId: params.id,
                        itemSaleableId: item.id,
                        itemSaleablePriceListId: item.unique,
                        quantity: 1,
                        localPrice: item.price.value,
                        localLabel: item.label,
                        walletBalance: wallet?.balance || 0,
                        corporateCreditBalance: corporateCredit?.balance || 0,
                        bundleId: params.bundleId,
                      });
                    }
                  }
                }
                sessionStorage.setItem("bundleRecapAllowed", "true");
                history.push(`${url}/recap${location.search}`, { selectedByCategory });
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "AGGIUNGI AL CARRELLO"}
          </Button>}
        </div>
      )}
    </div>
  );
}
